import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { api } from '../../lib/api';
import {
  EMPTY_FILTERS,
  buildGrid,
  filterGridRows,
  gridCauses,
  staleSourceFindings,
  sortGridRows,
  summariseGrid,
  type GridAccount,
  type GridFilters,
  type GridPreset,
  type GridRow,
} from '../../lib/sourceHealth/grid';
import {
  MATRIX_SOURCES,
  MATRIX_SOURCE_LABEL,
  cellStatus,
  isCancelled,
  type MatrixCell,
  type MatrixSourceKey,
} from '../../lib/sourceHealth/matrix';
import type { HealthSourceKey, SourceHealth } from '../../types/sourceHealth';
import type { ReportingPullMode } from '../../types/reporting';
import { triggerBackfill } from './api';
import { pullModeLabel } from './backfillView';
import { formatRelativeAge } from './syncHealthView';

// THE COVERAGE GRID — rows are accounts, columns are sources, cells are dots.
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md D16-D20
//
// ⚠️⚠️ EVERY DECISION IS UPSTREAM in `src/lib/sourceHealth/grid.ts` and
// `matrix.ts` so it can be tested without jsdom — this suite sits close to the
// documented per-test timeout cliff, and the rules here are the kind that fail
// silently. This file renders and nothing else: it computes no verdict, decides
// no colour and infers no enablement.
//
// ⚠️ IT ABSORBS `/reporting/coverage`: status and action in one place.

interface GridPayload {
  accounts: GridAccount[];
  sources: SourceHealth[];
  failures: Array<{ source: string; error: string }>;
  demographics: SourceHealth | null;
}

// ⚠️ `missing` and `stillSyncing` carry NO colour — see `cellStatus`. They are
// distinguished by SHAPE instead (a ring, a hollow square), so the page can show
// them without asserting a severity nobody has measured, and so a colour-blind
// reader is not relying on hue for the two states that are not red/amber/green.
const CELL_CLASS: Record<MatrixCell['kind'], string> = {
  health: '',
  // ⚠️ Drift never reaches this map — `cellStatus` gives it red, so it renders
  // as a dot like any other red. The entry exists because the Record is
  // exhaustive, which is what makes a NEW cell kind a compile error here rather
  // than an invisible blank square.
  drift: '',
  notEnabled: 'text-muted-foreground',
  unknown: 'border-2 border-dashed border-muted-foreground/60 rounded-full',
  missing: 'border-2 border-violet-500 rounded-full',
  stillSyncing: 'border-2 border-sky-500 rounded-sm',
  decommissioned: 'text-muted-foreground',
};

// ⚠️ THE EXISTING MODES AND THE EXISTING LABELS. D22b says move the control,
// not rebuild it — `PULL_MODES` and `pullModeLabel` are the Pull Control page's
// own, so the two screens cannot offer different depths under the same words.
const PULL_MODES: ReportingPullMode[] = [
  'structural-recent',
  'structural-full',
  'demographics-recent',
  'demographics-full',
];

const DOT: Record<string, string> = {
  red: 'bg-rose-500',
  amber: 'bg-amber-500',
  green: 'bg-kyck-500',
};

function Cell({ cell }: { cell: MatrixCell }): React.ReactElement {
  const status = cellStatus(cell);
  // ⚠️ "–" IS A REAL GLYPH, not an empty cell. An empty cell reads as a
  // rendering fault; the dash says "we deliberately do not pull this".
  if (cell.kind === 'notEnabled' || cell.kind === 'decommissioned') {
    return (
      <span className="text-muted-foreground" title={cell.reason}>
        –
      </span>
    );
  }
  return (
    <span
      className={`inline-block h-3 w-3 ${status !== null ? `rounded-full ${DOT[status]}` : CELL_CLASS[cell.kind]}`}
      title={cell.reason ?? ''}
      aria-label={cell.kind === 'health' ? status! : cell.kind}
    />
  );
}

/**
 * ⚠️⚠️ THE META BACKFILL, MOVED NOT REBUILT (D22b). It fires the SAME
 * `triggerBackfill` the Pull Control page fires, with the SAME `PULL_MODES` and
 * the SAME labels — one endpoint, one vocabulary, one audit trail.
 *
 * ⚠️ §12's invariant: an action is offered ONLY where it exists. Meta has a
 * one-click backfill; Google Ads and CRM do not, and their lines say where to go
 * instead. See `GoogleCauses` for why Google's absence is deliberate rather than
 * unfinished.
 *
 * ⚠️ A SHARED CLUSTER IS NAMED ON THE CONTROL. A Meta backfill pulls the AD
 * ACCOUNT, so on a shared cluster it rewrites every member's rows — a smaller
 * version of the same blast-radius problem that keeps a button off the Google
 * cells, and the reader deserves to know before they click rather than after.
 */
function MetaBackfill({ row }: { row: GridRow }): React.ReactElement | null {
  const qc = useQueryClient();
  const [mode, setMode] = useState<ReportingPullMode>('structural-full');
  const meta = row.detail.find((d) => d.source === 'meta');
  const mutation = useMutation({
    mutationFn: () => triggerBackfill(row.account.accountId, { pullMode: mode }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['source-health-grid'] }),
  });

  // ⚠️ Offered only where the pull can actually address the account. An unwired
  // account cannot be backfilled — the fix there is the wiring, which its cell
  // already says.
  if (row.account.inRoster.meta !== true) return null;
  if (meta === undefined || meta.cell.kind === 'notEnabled') return null;

  const shared = meta.sharedWith.length;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <select
        aria-label="Backfill depth"
        className="rounded border border-border px-2 py-1 text-sm"
        value={mode}
        onChange={(e) => setMode(e.target.value as ReportingPullMode)}
      >
        {PULL_MODES.map((m) => (
          <option key={m} value={m}>
            {pullModeLabel(m)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="rounded border border-kyck-600 px-3 py-1 text-sm font-medium text-kyck-700 disabled:opacity-50"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? 'Starting…' : 'Backfill Meta Ads'}
      </button>
      {shared > 0 && (
        <span className="text-xs text-amber-700">
          ⚠️ Shared ad account — this also rewrites {shared} other{' '}
          {shared === 1 ? 'account' : 'accounts'}.
        </span>
      )}
      {mutation.isError && (
        <span className="text-xs text-rose-700">
          Could not start: {(mutation.error as Error).message}
        </span>
      )}
      {mutation.isSuccess && <span className="text-xs text-kyck-700">Started.</span>}
    </div>
  );
}

function RowDetail({ row }: { row: GridRow }): React.ReactElement {
  return (
    <tr className="bg-muted/30">
      <td colSpan={MATRIX_SOURCES.length + 2} className="px-4 py-3">
        {/* ⚠️ THE WORKLIST'S OWN REASONING IS THE ROW DETAIL — it was not
            replaced by the grid, it became this. */}
        <ul className="space-y-2">
          {row.detail.map((line) => (
            <li key={line.source} className="flex items-start gap-3 text-sm">
              <Cell cell={line.cell} />
              <div className="min-w-0">
                <span className="font-medium">{MATRIX_SOURCE_LABEL[line.source]}</span>{' '}
                <span className="text-muted-foreground">
                  {line.cell.kind === 'health' ? (line.cell.reason ?? 'Current.') : line.cell.reason}
                </span>
                {line.dataThrough !== null && (
                  <span className="text-muted-foreground"> · data to {line.dataThrough}</span>
                )}
                {line.sharedWith.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Shares a pull with {line.sharedWith.join(', ')}
                  </div>
                )}
                {line.action !== null && (
                  <div>
                    {line.action}{' '}
                    {/* ⚠️ A LINK ONLY WHERE A REAL CONTROL EXISTS. Most of these
                        are repaired elsewhere, and a button that does nothing is
                        worse than a sentence saying where to go. */}
                    {line.href !== null && (
                      <a className="text-kyck-600 underline underline-offset-2" href={line.href}>
                        Open
                      </a>
                    )}
                  </div>
                )}
                {/* ⚠️⚠️ THE CLIENT-VISIBLE CONSEQUENCE, ON THE SOURCE'S OWN LINE.
                    A prior year missing ONE month withholds the WHOLE
                    year-on-year line from the client's report — and it does so
                    PER CHANNEL, which is why this sits here rather than once per
                    row. A single row-level note let one source answer for
                    another: it MASKED a verdict, and the Google causes panel
                    then counted Meta shortfalls (measured 2026-09-22: 110 of
                    152 had no Google Ads at all).

                    ⚠️ Shown only where it is CERTAIN — see `yearOnYearForSource`;
                    its silence must not be read as "this account has a chart".

                    ⚠️⚠️ TWO REASONS, TWO TREATMENTS. `pending` is amber because
                    somebody has to re-run a pull; `noPriorActivity` is MUTED
                    because the account simply was not advertising and there is
                    nothing to do — painting that amber is what left 57 of 58
                    Google accounts flagged with no action available, which is
                    how a worklist becomes wallpaper. */}
                {line.yearOnYear !== null && (
                  <div
                    className={
                      line.yearOnYear.kind === 'pending'
                        ? 'mt-1 text-amber-800'
                        : 'mt-1 text-muted-foreground'
                    }
                  >
                    {line.yearOnYear.message}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
        <MetaBackfill row={row} />
      </td>
    </tr>
  );
}

function Row({ row }: { row: GridRow }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const cancelled = isCancelled(row.account);
  return (
    <>
      <tr className="border-b border-border last:border-0">
        <td className="px-4 py-2">
          <button
            type="button"
            className="flex items-start gap-1.5 text-left"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {/* ⚠️ THE ROW OPENS, AND IT HAS TO LOOK LIKE IT DOES (D22a4). A
                clickable row with no affordance is a feature nobody finds — the
                detail is where every reason and every action lives, so an
                undiscovered disclosure hides the whole point of the page. */}
            <span
              aria-hidden
              className={`mt-0.5 inline-block text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
            >
              ›
            </span>
            <span className="font-medium text-foreground">{row.account.accountName}</span>
            {/* ⚠️ THE CANCELLED MARKER IS VISIBLE WITHOUT OPENING THE ROW
                (D20b#1) — a green cell means the opposite thing on this row, so
                the reader has to know before they read the dots. */}
            {cancelled && (
              <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-800">
                Cancelled
              </span>
            )}
            {cancelled && row.liveSources.length > 0 && (
              <span className="ml-2 text-xs text-sky-700">
                still syncing: {row.liveSources.map((s) => MATRIX_SOURCE_LABEL[s]).join(', ')}
              </span>
            )}
          </button>
        </td>
        {MATRIX_SOURCES.map((s) => (
          <td key={s} className="px-4 py-2 text-center">
            <Cell cell={row.cells[s]} />
          </td>
        ))}
        <td className="px-4 py-2 text-xs text-muted-foreground">{row.account.accountType}</td>
      </tr>
      {open && <RowDetail row={row} />}
    </>
  );
}

const PRESETS: Array<{ key: GridPreset; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'needsAttention', label: 'Needs attention' },
  { key: 'adAccountDrift', label: 'Ad-account drift' },
  { key: 'cancelledStillSyncing', label: 'Cancelled and still syncing' },
  { key: 'signedUpNeverWired', label: 'Signed up, never wired' },
];

/** A multi-select that reads the options off the rows, so it can never offer a
 *  value nothing has. */
function FilterSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: readonly string[];
  onChange: (v: string[]) => void;
}): React.ReactElement {
  return (
    <select
      aria-label={label}
      className="rounded border border-border px-2 py-1.5 text-sm"
      value={selected[0] ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? [] : [e.target.value])}
    >
      <option value="">{label}: all</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

export function CoverageGridPage(): React.ReactElement {
  const query = useQuery({
    queryKey: ['source-health-grid'],
    queryFn: () => api<GridPayload>('/api/source-health/grid'),
  });
  const [filters, setFilters] = useState<GridFilters>(EMPTY_FILTERS);

  const { rows, summary } = useMemo(() => {
    const payload = query.data;
    if (payload === undefined) return { rows: [] as GridRow[], summary: null };
    // ⚠️⚠️ THE DEMOGRAPHICS HEALTH IS HANDED OVER WHOLE so `buildGrid` can run
    // it through the SAME `accountStatus` as every other column. An earlier
    // draft of this page mapped it to a hardcoded green — which would have
    // reported perfect health for a pull that has no scheduler at all.
    const built = buildGrid({
      accounts: payload.accounts,
      sources: payload.sources,
      failedSources: payload.failures
        .map((f) => f.source)
        .filter((s): s is HealthSourceKey => s === 'meta' || s === 'crm' || s === 'googleAds'),
      demographics: payload.demographics,
      nowMs: Date.now(),
    });
    return {
      rows: sortGridRows(built),
      summary: summariseGrid(
        built,
        payload.failures.map((f) => f.source as HealthSourceKey),
        payload.sources,
      ),
    };
  }, [query.data]);

  const visible = useMemo(() => filterGridRows(rows, filters), [rows, filters]);

  // ⚠️ Options come from the ROWS, so a filter can never offer a value nothing
  // has — and the list shrinks honestly as the row set does.
  const facets = useMemo(() => {
    const uniq = (pick: (r: GridRow) => string | null): string[] =>
      [...new Set(rows.map(pick).filter((v): v is string => v !== null))].sort();
    return {
      brands: uniq((r) => r.account.brandName),
      groups: uniq((r) => r.account.groupName),
      managers: uniq((r) => r.account.accountManagerName),
    };
  }, [rows]);

  // ⚠️ Derived from the SAME rows the grid renders, so the count cannot claim to
  // have absorbed a finding still on screen.
  const staleSources = useMemo(
    () => (query.data === undefined ? [] : staleSourceFindings(rows, query.data.sources, Date.now())),
    [rows, query.data],
  );

  const shortfalls = useMemo(
    () =>
      query.data === undefined
        ? { causes: [], uncaused: 0 }
        : gridCauses(rows, query.data.sources),
    [rows, query.data],
  );

  return (
    <>
      <PageHeader
        title="Coverage"
        description="Every account, every source, and what needs doing about it."
      />

      {query.isLoading && <Spinner />}

      {/* ⚠️⚠️ A FAILED SOURCE IS NAMED. Dropping it silently yields a shorter
          grid, which reads as better news — the inversion this page exists to
          prevent. */}
      {(query.data?.failures.length ?? 0) > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <p className="font-medium">Some sources could not be read.</p>
          <ul className="mt-1 list-disc pl-5">
            {query.data?.failures.map((f) => (
              <li key={f.source}>
                {f.source}: {f.error}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-muted-foreground">
            Their columns show as unknown, not as healthy.
          </p>
        </div>
      )}

      {/* ⚠️⚠️ ONE PIPELINE FAILURE, ONE LINE (§5, 2026-09-22). When a source's own
          pull is behind, every account under it is stale for the SAME reason —
          and reporting that per account turned a two-hour Meta outage into 234
          amber rows, which is how a worklist becomes wallpaper for a morning.
          The Google transfer has collapsed to one line per cause since D22d;
          this is the same treatment for the source itself.

          ⚠️ IT STATES WHAT IT ABSORBED. Without the count this is a bare "something
          is wrong"; with it, it carries exactly what the suppressed rows were
          saying. And the per-account fact stays reachable — every detail line
          still shows `data to <date>`. */}
      {staleSources.length > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <p className="font-medium">A source&rsquo;s own pull is behind.</p>
          <ul className="mt-1 space-y-1">
            {staleSources.map((f) => (
              <li key={f.source}>
                <span className="font-medium">{f.jobLabel}</span> — {f.reason}
                {/* ⚠️ A RELATIVE AGE, not the raw ISO string. "Last run 4 hours
                    ago" is the fact an operator acts on; a UTC timestamp makes
                    them do the arithmetic, and do it in the wrong timezone. */}
                {f.lastRunAt !== null && (
                  <span className="text-muted-foreground">
                    {' '}
                    Last run {formatRelativeAge(f.lastRunAt, Date.now()) ?? f.lastRunAt}.
                  </span>
                )}
                {f.accountsAffected > 0 && (
                  <span className="text-muted-foreground">
                    {' '}
                    {f.accountsAffected} account{f.accountsAffected === 1 ? '' : 's'} would
                    otherwise read as stale for this one reason; shown here instead of on each
                    row.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary !== null && (
        <div className="mb-4 flex flex-wrap gap-2">
          {PRESETS.map((p) => {
            const count =
              p.key === 'needsAttention'
                ? summary.needsAttention
                : p.key === 'adAccountDrift'
                  ? summary.adAccountDrift
                  : p.key === 'cancelledStillSyncing'
                    ? summary.cancelledStillSyncing
                    : p.key === 'signedUpNeverWired'
                      ? summary.signedUpNeverWired
                      : summary.totalRows;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, preset: p.key }))}
                className={`rounded border px-3 py-1.5 text-sm ${
                  filters.preset === p.key
                    ? 'border-kyck-600 bg-kyck-50 font-medium'
                    : 'border-border'
                }`}
              >
                {p.label} <span className="text-muted-foreground">{count}</span>
              </button>
            );
          })}
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search accounts"
            aria-label="Search accounts"
            className="rounded border border-border px-3 py-1.5 text-sm"
          />
          <FilterSelect
            label="Brand"
            options={facets.brands}
            selected={filters.brands}
            onChange={(brands) => setFilters((f) => ({ ...f, brands }))}
          />
          <FilterSelect
            label="Group"
            options={facets.groups}
            selected={filters.groups}
            onChange={(groups) => setFilters((f) => ({ ...f, groups }))}
          />
          <FilterSelect
            label="Account manager"
            options={facets.managers}
            selected={filters.accountManagers}
            onChange={(accountManagers) => setFilters((f) => ({ ...f, accountManagers }))}
          />
        </div>
      )}

      {/* ⚠️⚠️ THE GOOGLE ADS SCOPE, STATED, WITH NO BUTTON (D22c/D22d). There is
          no per-customer backfill in Data Transfer: the unit is the transfer
          CONFIG, one per MCC, and a historical re-run rewrites EVERY customer
          under it — ~18 hours, measured, and not shortenable by splitting. A
          control on a Google Ads cell would therefore fire an MCC-wide job
          affecting N accounts the row does not name, which is worse than no
          control: the action would exist, but its blast radius would not match
          the row it sits on. So the scope is shown and the action is not. */}
      {(shortfalls.causes.length > 0 || shortfalls.uncaused > 0) && (
        <div className="mb-4 rounded border border-border px-4 py-3 text-sm">
          {/* ⚠️ "WAITING ON PRIOR-YEAR DATA", not "short of the window" (D22e).
              The old wording asserted a deficiency in OUR pipeline, and for 57 of
              the 58 accounts it covered that was false — they simply had not
              advertised. Those accounts are no longer counted here at all.

              ⚠️⚠️ AND THIS PANEL IS GOOGLE'S ALONE. `gridCauses` reads each row's
              GOOGLE verdict by name (`googleYearOnYear`). While it read a
              row-level one, a Meta shortfall counted here: measured live
              2026-09-22 it showed 42 causes + 110 uncaused, of which exactly ONE
              was genuinely Google. A Meta shortfall is real and actionable — it
              is simply not a transfer-config problem, and it needs its own
              surface rather than this one's heading. */}
          <p className="font-medium">Google Ads accounts waiting on prior-year data</p>
          <ul className="mt-1 space-y-1">
            {shortfalls.causes.map((c) => (
              <li key={c.causeKey}>
                {c.causeLabel} — {c.shortfallCount} of {c.accountCount}{' '}
                {c.accountCount === 1 ? 'account' : 'accounts'} waiting on prior-year data.
              </li>
            ))}
          </ul>
          {/* ⚠️⚠️ THE REMAINDER IS NAMED. A panel whose lines sum to less than the
              rows beneath it looks complete while understating itself — the
              silent shortfall this page exists to prevent. */}
          {shortfalls.uncaused > 0 && (
            <p className="mt-1">
              {shortfalls.uncaused}{' '}
              {shortfalls.uncaused === 1 ? 'further account is' : 'further accounts are'} waiting
              on prior-year data with no transfer config identified.
            </p>
          )}
          <p className="mt-2 text-muted-foreground">
            There is no per-account backfill for Google Ads. The unit is the transfer config —
            one per MCC — and a historical re-run rewrites every account under it (~18 hours).
            No control is offered here because its effect would be wider than any one row.
          </p>
        </div>
      )}

      {query.data !== undefined && (
        <table className="w-full text-sm">
          {/* ⚠️ STICKY HEADER (D22a3). The documented gotcha applies: a header
              sticks to its nearest SCROLLING ancestor, so with no bounded
              scrollport it would leave with the page. `sticky top-0` on the
              cells (not the row — a collapsed border is not painted on a stuck
              header) plus the page's own scroll is what holds it; the shadow is
              an inset one for the same reason. */}
          <thead className="sticky top-0 z-10 bg-background shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
            <tr className="text-left">
              <th className="px-4 py-2 font-medium">Account</th>
              {MATRIX_SOURCES.map((s) => (
                <th key={s} className="px-4 py-2 text-center font-medium">
                  {MATRIX_SOURCE_LABEL[s]}
                </th>
              ))}
              <th className="px-4 py-2 font-medium">Type</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <Row key={row.account.accountId} row={row} />
            ))}
          </tbody>
        </table>
      )}

      {/* ⚠️⚠️ AN EMPTY GRID IS NEVER "ALL CLEAR". No rows means either nothing
          loaded or nothing matched the filter, and both read as good news on a
          page whose job is bad news. `summary.allClear` is the only thing
          allowed to say so, and it requires rows, no failures and no source
          returning zero accounts. */}
      {query.data !== undefined && visible.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {summary?.allClear === true
            ? 'Nothing needs attention.'
            : 'No accounts match this view. That is not the same as nothing being wrong.'}
        </p>
      )}
    </>
  );
}
